import type { FC, MouseEvent } from 'react';
import { useTracking } from 'react-tracking';
import NotificationTag from '@components/NotificationTag';
import { SWS_SUPPORT } from '@/constants';
import {
  NavItem,
  NavList,
  NavWrapper,
  NavItemHREF,
  Notification,
  AccountNavigation,
  ViewCompaniesLink,
} from './styled';
import {
  ROUTE_USER_PROFILE,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_LOGOUT,
  ROUTE_USER_BETA_LAB,
  ROUTE_PLANS,
  ROUTE_USER_SUBSCRIPTION,
} from '@/constants/routes';

import { startCase } from 'lodash';
import { useQuery } from 'react-query';
import { getStripePortalQuery } from '@/components/OurPlans/components/Plans/components/PlanButton/query';
import {
  isLegacyPlan,
  shouldSeeSubscriptionModal,
} from '@/components/OurPlans/components/Plans/utils';
import useUser from '@/hooks/useUser';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import { useUserAccess } from '@/hooks/useUserAccess';
import type { ActiveModal } from '@/components/DesktopTopbar/components/DesktopTopbarAccountAccess';

export interface AccountDropDownMenuProp {
  maxViewCompanyReports: number;
  location: string;
  onActivateModal: (activeModal: ActiveModal) => void;
  onOpenStripePortal: (url: string) => void;
}
export const DesktopTopbarAccountDropdown: FC<AccountDropDownMenuProp> = ({
  maxViewCompanyReports,
  location,
  onActivateModal,
  onOpenStripePortal,
}) => {
  const limits = useCompanyLimits();
  const { data: userAccess } = useUserAccess();
  const { data: userData } = useUser();
  const { trackEvent } = useTracking({ subject: 'primaryNavDesktopDropdown' });

  const isTrialUser = userData.billing.status === 'trialing';

  const { data: stripePortal } = useQuery(
    ['STRIPE_PORTAL', undefined],
    getStripePortalQuery,
    {
      enabled: !isLegacyPlan(userAccess.planType),
      staleTime: 600000,
    }
  );

  const handleClick = (e: MouseEvent) => {
    const shouldSeeModal = shouldSeeSubscriptionModal(
      userData.hasSeenSubscriptionModal,
      userData.billing.currentPeriodStart,
      userAccess.planType,
      userAccess.provider
    );

    if (shouldSeeModal && stripePortal?.url) {
      e.preventDefault();
      onOpenStripePortal(stripePortal.url);
      onActivateModal('billing');
    }
  };

  return (
    <>
      <AccountNavigation>
        <NavWrapper>
          {maxViewCompanyReports !== -1 && (
            <Notification>
              <p>
                <span>You’ve used </span>
                <NotificationTag alert data-cy-id="company-view-limit-count">
                  {`${limits.views}/${limits.limit}`}
                </NotificationTag>
                <span>
                  {` spots of your ${startCase(
                    userAccess.planType
                  )} Plan’s company view limit`}
                </span>
              </p>
              <p>
                <ViewCompaniesLink
                  data-cy-id="account-nav-viewed-companies"
                  onClick={() => {
                    onActivateModal('viewedCompanies');
                  }}
                >
                  View Companies
                </ViewCompaniesLink>
              </p>
            </Notification>
          )}
          <NavList hasLimit={maxViewCompanyReports !== -1}>
            <li>
              <NavItem
                to={{ type: ROUTE_USER_PROFILE }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-profile"
                onClick={() =>
                  location !== ROUTE_USER_PROFILE &&
                  trackEvent({ modifier: 'Profile' })
                }
              >
                Profile
              </NavItem>
            </li>
            {RUNTIME_ENV !== 'native' &&
              !isTrialUser &&
              !isLegacyPlan(userAccess.planType) &&
              userAccess.provider === 'stripe' && (
                <li>
                  <NavItemHREF
                    as="a"
                    styleType="softLink"
                    rel="noopener noreferrer"
                    href={stripePortal?.url}
                    data-cy-id="account-nav-billing"
                    onClick={(e) => {
                      handleClick(e);
                      trackEvent({ modifier: 'Billing' });
                    }}
                  >
                    Subscription &amp; Billing
                  </NavItemHREF>
                </li>
              )}
            {RUNTIME_ENV !== 'native' && userAccess.provider !== 'stripe' && (
              <li>
                <NavItem
                  to={{ type: ROUTE_USER_SUBSCRIPTION }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-subscription"
                  onClick={() => {
                    location !== ROUTE_USER_SUBSCRIPTION &&
                      trackEvent({ modifier: 'Subscription' });
                  }}
                >
                  Manage Subscription
                </NavItem>
              </li>
            )}
            {RUNTIME_ENV !== 'native' && userAccess.provider === 'stripe' && (
              <li>
                <NavItem
                  to={{ type: ROUTE_PLANS }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-plans"
                  onClick={() => trackEvent({ modifier: 'Plans' })}
                >
                  Plans &amp; Pricing
                </NavItem>
              </li>
            )}

            <li>
              <NavItem
                to={{ type: ROUTE_USER_NOTIFICATIONS }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-notifications"
                onClick={() =>
                  location !== ROUTE_USER_NOTIFICATIONS &&
                  trackEvent({ modifier: 'Notifications' })
                }
              >
                Notifications
              </NavItem>
            </li>
            {RUNTIME_ENV !== 'native' && (
              <li>
                <NavItem
                  to={{ type: ROUTE_USER_BETA_LAB }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-notifications"
                  onClick={() =>
                    location !== ROUTE_USER_BETA_LAB &&
                    trackEvent({ modifier: 'Beta Lab' })
                  }
                >
                  Beta Lab
                </NavItem>
              </li>
            )}
            <li>
              <NavItemHREF
                as="a"
                styleType="softLink"
                target="_blank"
                rel="noopener noreferrer"
                href={SWS_SUPPORT}
                data-cy-id="account-nav-help"
                onClick={() => trackEvent({ modifier: 'Help Center' })}
              >
                Help Center
              </NavItemHREF>
            </li>
            <li>
              <NavItem
                to={{ type: ROUTE_USER_LOGOUT }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-logout"
                onClick={() => trackEvent({ modifier: 'Log out' })}
              >
                Logout
              </NavItem>
            </li>
          </NavList>
        </NavWrapper>
      </AccountNavigation>
    </>
  );
};
